import { REDUX_DEFAULT_DOMAIN_MAP_KEY } from 'modules/redux/constants'

import getAssociationKeysReduxUtil from 'modules/redux/utils/get-associations-keys'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import { formatInstanceBeforeCreate } from 'models/formatters/before-create'
import { updateDataReduxUtil } from 'modules/redux/utils/legacy/update-data'
import { isPointerReduxUtil } from 'modules/redux/utils/legacy/is-pointer'
import { getDomainPathReduxUtil } from 'modules/redux/utils/legacy/get-domain-path'
import getDomainMapKeyFromPathReduxUtil from 'modules/redux/utils/get-domain-map-key-from-path'

/**
 * A mapping of className to cid count.
 * @private
 * @type {Object}
 */
const cid = {}

function getDomainMapKeyFromClassNameReduxUtil (className) {
  const domainPath = getDomainPathReduxUtil(className)
  return getDomainMapKeyFromPathReduxUtil(domainPath)
}

/**
 * Creates a instance from the given attributes.
 * @param  {string} className
 * @param  {Object} [params={}]
 *         {Object} [data={}]
 *         {Status} [status=null]
 *         {Object} [changed={}]
 * @returns {Instance}
 */
function createReduxUtil (className, params = {}) {
  params = { ...params }
  let {
    data = {},
    status = null,
    changed = {}
  } = params
  data = {
    ...data,
    className
  }

  // Format any data that needs to be formatted
  data = formatInstanceBeforeCreate({
    data,
    className
  })

  // Create the associations
  data = createAssociationsReduxUtil({
    data,
    status,
    className
  })

  return {
    changed,
    error: null,
    validation: null,
    status: status,
    className,
    data,
    cid: setCidReduxUtil(className)
  }
}

/**
 * Increment or set the cid of the given className.
 * @private
 * @param  {className} className
 * @returns {number}
 */
function setCidReduxUtil (className) {
  if (!cid[className]) {
    cid[className] = 0
  }
  // Increment the count
  cid[className]++
  // Return the count
  return cid[className]
}

/**
 * Creates an array of instances.
 * @private
 * @param  {Object[]} data
 * @param  {Status} status
 * @returns {Object[]}
 */
function createListReduxUtil ({ data, status }) {
  return (
    data || []
  ).map(child => {
    // If it's already a legacy instance, noop
    if (isInstanceReduxUtil(child)) {
      return child
    }

    if (isPointerReduxUtil(child)) {
      return child
    }

    // If it's not a real database instance, noop
    if (!child.className) {
      return child
    }

    return createReduxUtil(child.className, {
      status,
      data: child
    })
  })
}

/**
 * Creates the instance's associations.
 *
 * NOTE: Mutative
 *
 * @param  {Object|null} data
 * @param  {Status} status
 * @param  {string} className
 * @returns {Object}
 */
function createAssociationsReduxUtil ({ data, status, className }) {
  // If there is no data, noop
  // e.g.
  if (!data) {
    return data
  }

  // Get the keys
  const associationKeys = getAssociationKeysReduxUtil(className)
  if (!associationKeys.length) {
    return data
  }

  // Go through all the associations and create instances
  associationKeys.forEach(associationKey => {
    const association = data[associationKey]
    if (!association) {
      return
    }
    if (isPointerReduxUtil(association)) {
      return
    }
    if (isInstanceReduxUtil(association)) {
      return
    }

    if (Array.isArray(association)) {
      data[associationKey] = createListReduxUtil({
        data: association,
        status
      })
    } else {
      data[associationKey] = createReduxUtil(association.className, {
        data: association,
        status
      })
    }
  })
  return data
}

/**
 * Creates a pointer of the given instance.
 * NOTE: Recursively converts all associated instances into pointers.
 *
 * @param  {Instance} instance
 * @returns {Pointer}
 */
function createPointerReduxUtil (instance) {
  if (!isInstanceReduxUtil(instance)) {
    return instance
  }
  // Get the instance's `className` and `data`
  const { className, data } = instance
  // Create the pointer
  const domainMapKey = getDomainMapKeyFromClassNameReduxUtil(className)
  let pointer = {
    data: {
      id: getIdReduxUtil(instance),
      className
    },
    className
  }
  const domainMapValue = data[domainMapKey]
  if (domainMapKey !== REDUX_DEFAULT_DOMAIN_MAP_KEY && domainMapValue) {
    pointer.data[domainMapKey] = data[domainMapKey]
  }
  // Create pointers for all the associations
  const associationKeys = getAssociationKeysReduxUtil(className)
  associationKeys.forEach(associationKey => {
    // Get the association instance(s)
    const association = data[associationKey]
    // If the association is an array of instances,
    // convert them into pointers
    if (Array.isArray(association)) {
      pointer = updateDataReduxUtil(pointer, {
        [associationKey]: association.map(createPointerReduxUtil)
      })
    } else if (association) {
      // Convert the association instance into a pointer
      pointer = updateDataReduxUtil(pointer, {
        [associationKey]: createPointerReduxUtil(association)
      })
    }
  })
  return pointer
}

export {
  createReduxUtil,
  createPointerReduxUtil,
  createAssociationsReduxUtil
}
